import { useEffect } from "react";

import { SxProps, Theme } from "@mui/material";
import { FormType } from "@simplyk/common";

import { ShareFormModal } from "./ShareFormModal";

import { ChooseEmbedTypeTab } from "@/components/ShareFormModal/ChooseEmbedTypeTab";
import {
  ButtonPopupEmbedInstructionsTab,
  FormEmbedInstructionsTab,
  LeaderboardEmbedInstructionsTab,
  LinkedButtonEmbedInstructionsTab,
  ThermometerEmbedInstructionsTab,
} from "@/components/ShareFormModal/EmbedInstructionsTab";
import { ErrorTab } from "@/components/ShareFormModal/ErrorTab";
import { HomeTab } from "@/components/ShareFormModal/HomeTab";
import { HomeTabSkeleton } from "@/components/ShareFormModal/HomeTabSkeleton";
import { QrCodeTab } from "@/components/ShareFormModal/QrCodeTab";
import {
  RootLevelShareFormModalTab,
  useConnectedShareFormModalState,
} from "@/components/ShareFormModal/useConnectedShareFormModalState";
import { AmplitudeEvents } from "@/constants/amplitude";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";

type ConnectedShareFormModalProps = {
  formId: string;
  formType: FormType;
  open: boolean;
  onClose?: () => void;
  defaultTab?: RootLevelShareFormModalTab;
  source?:
    | "form_donator_view"
    | "liveFormEditor"
    | "donationFormStepper"
    | "formsTable"
    | "campaignsFormsTable"
    | "ticketingStepper"
    | "campaignOverviewTab";
  hideViewFormButton?: boolean;
  sx?: SxProps<Theme>;
};

export const ConnectedShareFormModal = ({
  formId,
  formType,
  open,
  onClose,
  source,
  hideViewFormButton,
  sx,
}: ConnectedShareFormModalProps) => {
  const { logAmplitudeEvent } = useAmplitude();

  const {
    isLoading,
    hasError,
    isOnboarding,
    isFormCreation,
    currentTab,
    formLink,
    formPath,
    formCategory,
    formIsPrimaryCampaign,
    formIsPrimaryTeam,
    onGoToQrCode,
    onGoToChooseEmbedType,
    defaultCollapseAdditionalSharingOptions,
    canCollapseAdditionalSharingOptions,
    onExpandAdditionalSharingOptions,
    onCollapseAdditionalSharingOptions,
    showTitleByFormCategory,
    onGoBackHome,
    onChooseEmbedType,
    defaultEmbedChoice,
    availableEmbedChoices,
    reset,
    onInvitationEmailActionClick,
    isGivingTuesday,
    shouldShowInvitationEmailAction,
  } = useConnectedShareFormModalState({
    formId,
    formType,
    defaultTab: "Home",
  });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (open && formCategory && source) {
      logAmplitudeEvent(AmplitudeEvents.ShareFormModalOpen, { location: source, form_type: formCategory });
    }
  }, [formCategory, logAmplitudeEvent, open, source]);

  return (
    <ShareFormModal open={open} onClose={onClose} sx={sx}>
      {isLoading ? (
        <HomeTabSkeleton onClose={onClose} isOnboarding={isOnboarding} />
      ) : hasError ? (
        <ErrorTab onClose={onClose} />
      ) : (
        <>
          {currentTab === "Home" && (
            <HomeTab
              mode={isFormCreation ? "form-creation" : "default"}
              formLink={formLink!}
              formCategory={formCategory}
              canCollapseAdditionalSharingOptions={canCollapseAdditionalSharingOptions}
              defaultCollapseAdditionalSharingOptions={defaultCollapseAdditionalSharingOptions}
              onExpandAdditionalSharingOptions={onExpandAdditionalSharingOptions}
              onCollapseAdditionalSharingOptions={onCollapseAdditionalSharingOptions}
              isTitleByFormCategory={showTitleByFormCategory}
              optionalActions={{
                invitationEmail: shouldShowInvitationEmailAction
                  ? {
                      mode: isGivingTuesday ? "giving-tuesday" : "default",
                      onClick: onInvitationEmailActionClick,
                    }
                  : undefined,
              }}
              onQrCodeActionClick={onGoToQrCode}
              onEmbedActionClick={onGoToChooseEmbedType}
              onClose={onClose}
              hideViewFormButton={hideViewFormButton}
            />
          )}
          {currentTab === "QrCode" && (
            <QrCodeTab formLink={formLink!} onBackClick={onGoBackHome} formCategory={formCategory!} onClose={onClose} />
          )}
          {currentTab === "ChooseEmbedType" && (
            <ChooseEmbedTypeTab
              availableEmbedChoices={availableEmbedChoices}
              defaultChoice={defaultEmbedChoice}
              onChooseClick={onChooseEmbedType}
              onBackClick={onGoBackHome}
              onClose={onClose}
            />
          )}
          {currentTab === "EmbedFormInstructions" && (
            <FormEmbedInstructionsTab
              formId={formId}
              formPath={formPath!}
              formType={formType}
              formCategory={formCategory!}
              isPrimaryCampaign={formIsPrimaryCampaign}
              isPrimaryTeam={formIsPrimaryTeam}
              onBackClick={onGoToChooseEmbedType}
              onClose={onClose}
            />
          )}
          {currentTab === "EmbedLinkedButtonInstructions" && (
            <LinkedButtonEmbedInstructionsTab
              formLink={formLink!}
              formCategory={formCategory!}
              onBackClick={onGoToChooseEmbedType}
              onClose={onClose}
            />
          )}
          {currentTab === "EmbedButtonPopupInstructions" && (
            <ButtonPopupEmbedInstructionsTab
              formId={formId}
              formType={formType}
              isPrimaryCampaign={formIsPrimaryCampaign!}
              isPrimaryTeam={formIsPrimaryTeam!}
              formPath={formPath!}
              formCategory={formCategory!}
              onBackClick={onGoToChooseEmbedType}
              onClose={onClose}
            />
          )}
          {currentTab === "EmbedThermometerInstructions" && (
            <ThermometerEmbedInstructionsTab
              formId={formId}
              formPath={formPath!}
              formCategory={formCategory!}
              onBackClick={onGoToChooseEmbedType}
              onClose={onClose}
            />
          )}
          {currentTab === "EmbedLeaderboardInstructions" && (
            <LeaderboardEmbedInstructionsTab
              formId={formId}
              formPath={formPath!}
              formCategory={formCategory!}
              onBackClick={onGoToChooseEmbedType}
              onClose={onClose}
            />
          )}
        </>
      )}
    </ShareFormModal>
  );
};
